import type { CapturedNetworkRequest, PostHog } from "posthog-js";
import { isMaskedCrossOriginException } from "./lib/juicyads";
import { MASK_SELECTOR } from "./lib/replayMask";

/**
 * PostHog, wired for acquisition, navigation, the funnels and COARSE usage —
 * never per-clip. Identified for signed-in accounts, and only for those.
 *
 * IDENTITY (changed 2026-09-02; this file argued the opposite before)
 *
 * A signed-in user is identified by their account UUID, carrying `username` as a person
 * property so PostHog can show a name instead of a uuid. It was anonymous until now, and
 * the argument for that still stands on its own terms — a third-party profile tying a
 * name to porn-site activity is a liability. It was traded away deliberately, for two
 * things nothing else could give: knowing which accounts are actually retained, and being
 * able to keep the operator's own testing out of the numbers (that second one is
 * maintained in PostHog, as a cohort — see ARCHITECTURE.md, not this repo).
 *
 * What did NOT change is everything below: no clip, no watch history, no ids or
 * credentials in any string. The identity is a name against activity this file already
 * sent; it does not widen what that activity is. Anonymous visitors are still not
 * profiled at all (`person_profiles: "identified_only"`), so browsing without an account
 * remains exactly as unpersoned as it was.
 *
 * WHAT THIS DELIBERATELY DOES NOT SEND
 *
 * Not clip-level behaviour. What was watched, for how long, and WHICH clip was fapped or
 * collected already lives in `watch_events`, `clip_views`, `faps` and the playlists,
 * where it is complete and ours. This is an adult site, so per-clip viewing history is the
 * most sensitive data it holds and it does not leave our database. The engagement events
 * below count the ACT (a fap button was pressed, something was added to a collection) and
 * never name the clip — and `$pathname`, which the SDK stamps on every event, says where it
 * happened, scrubbed of ids on the way out.
 *
 * The split is deliberate rather than incidental: PostHog answers "who arrived, from
 * where, on what, and how far did they get", the local tables answer "what did they do
 * with it".
 *
 * Session recording is OFF on the wall for the same reason — a replay of the wall IS the
 * viewing history, frame by frame, attached to a person. It is ON for the control surfaces
 * (browse, collections, views, the remote, account flows): that is the UX worth replaying,
 * and a replay there shows layout and taps, never a clip — <video> and <img> are blocked
 * and every input masked. The route hook (useRouteAnalytics) is what flips it: recording
 * starts on a control route and STOPS on the wall, the pending screen, play/mint routes,
 * the admin panel and the credential-bearing routes, so the init default below stays
 * "off" and nothing records until a route says it may.
 *
 * On quota: this used to argue from volume (six regions advancing every ~19s is ~1,150
 * events per user-hour against a 1M/month tier). Measured in August 2026 the whole app
 * sent ~2.3k events a month — 0.2% of the tier — so volume is not what keeps clip-level
 * data out; the sensitivity is. The rate limits below are for bursts, not for budget.
 */

/**
 * Vite inlines these at BUILD time, so a production bundle must be built WITH them set.
 *
 * Read through a guarded reference rather than `import.meta.env.X` directly, because this
 * module is reachable from code that runs OUTSIDE Vite: tools/checks import client source
 * under tsx (rendition-ladder pulls in RegionPlayer, which imports this file), and there
 * `import.meta.env` is undefined, so a direct property read throws at import time and
 * takes the whole check with it. Vite still statically replaces `import.meta.env`, so the
 * inlining is unaffected.
 */
const ENV: Record<string, string | undefined> =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
const KEY = ENV.VITE_POSTHOG_KEY;
/**
 * Where the SDK sends: THIS origin, by default, not PostHog's.
 *
 * Brave's default blocklist (EasyPrivacy) blocks PostHog's ingestion path by HOST, and
 * only after the SDK's remote config has switched it there — so on Brave the first batch
 * of every page landed and every click after it vanished; both Patreon converters on
 * 2026-09-08 arrived with no funnel behind them. server/routes/analytics-relay.ts is the
 * same-origin path the lists have no rule for; it forwards bytes and keeps the cookies.
 * Absolute rather than "/api/relay" because the SDK compares hosts to decide what it is
 * talking to. VITE_POSTHOG_HOST still overrides, for a build that wants to go direct.
 */
const HOST =
  ENV.VITE_POSTHOG_HOST ??
  (typeof location !== "undefined" ? `${location.origin}/api/relay` : "/api/relay");

/**
 * Which PostHog project this build reports into.
 *
 * Dev and prod are SEPARATE PostHog projects with separate tokens, so local clicking
 * around cannot move production numbers. The guard is that absence is the default: with
 * no `VITE_POSTHOG_KEY` — which is every fresh checkout, since `.env*` is gitignored —
 * every function here is a no-op. Polluting prod requires someone to have deliberately
 * pasted the prod token into their local env, not merely to have forgotten a flag.
 *
 * `environment` is stamped on every event as a second line of defence: if the tokens ever
 * do get crossed, prod dashboards can still filter the noise out rather than being
 * silently wrong.
 *
 * Set EXPLICITLY rather than inferred from `import.meta.env.PROD`, which is true for any
 * `vite build` — including one run on a laptop. Inferring it would label a local
 * production build "production" and quietly undo the point of stamping it. `MODE` is the
 * fallback so a plain `vite dev` still says "development" without any configuration.
 */
const ENVIRONMENT = ENV.VITE_POSTHOG_ENV ?? ENV.MODE ?? "development";

/**
 * Every event this app sends, enumerated.
 *
 * A closed union rather than free-form strings: PostHog will happily accept a typo as a
 * brand-new event name, and the mistake only surfaces weeks later as a funnel that has
 * quietly measured nothing. Adding a name here is the moment to ask whether it is worth
 * the quota (see the budget note below).
 *
 * The set is built around this being a MULTI-DEVICE app: the activation moment is not
 * "signed up", it is "got a second screen playing", so the pairing funnel
 * (screen_code_shown -> screen_claimed -> wall_started) is the thing worth measuring.
 */
type AppEvent =
  // The gate: the pass/reject split is the top of every funnel below it.
  | "age_gate_passed"
  | "age_gate_rejected"
  /*
   * Pairing. `screen_code_shown` is ONE per code minted per page, not one per mint: a
   * code minted again after the server forgot it (a lapse, a restart) is
   * `screen_code_reissued`, rate-limited, so the funnel step is not also a heartbeat —
   * it was, and one television sent 618 of them in six days.
   *
   * The other half of a pairing — the claim landing — is captured by the SERVER
   * (server/src/analytics.ts, `screen_paired`): the code is shown by one browser and
   * redeemed by another, and no client-side funnel can join two anonymous browsers.
   */
  | "screen_code_shown"
  | "screen_code_reissued"
  | "screen_claimed"
  | "wall_started"
  | "screen_retargeted"
  // Control handoff: a QR minted on one device, redeemed on another.
  | "control_handoff_shown"
  | "control_adopted"
  // `view_saved` used to stand in for the account events, which live in the
  // 2026-08-22 funnel block below.
  | "view_saved"
  // Sessions and the remote.
  | "session_started"
  | "session_ended"
  // The goon card leaving the page — {source: recap|profile}: the recap a stop pops,
  // or the profile's history. Counted at the first door taken (share or download).
  | "session_card_downloaded"
  | "goontroller_connected"
  // Engagement: counts of the act, never the clip (see the header).
  // (`fap_clicked` was here; `fap_saved` below is the same act, with the surface on it.)
  | "added_to_collection"
  | "collection_created"
  | "tag_suggested"
  | "tag_reported"
  // Reliability.
  | "media_error"
  // A visible clip stopped making progress for STALL_ADVANCE_MS and the tile moved on
  // (RegionPlayer's stall watchdog). Carries `rung` and `reveal: "timeout" | "playing"`.
  | "media_stall"
  /*
   * The wall's decode budget changed (issues #13/#14, display/playbackBudget.ts):
   * {reason: probe|dropped_frames|decode_errors|recover, rung_ceiling, max_decoding, dropped_ratio,
   * tiles}. Numbers and a closed vocabulary — what was playing is never named. media_stall
   * and media_error carry the same budget numbers plus dpr / effective_dpr, so a stall can
   * be read against the ceiling it happened under.
   */
  | "playback_budget_changed"
  /*
   * Once a session, from the decode probe: the highest rung this device says it decodes
   * smoothly and in hardware (MediaCapabilities `powerEfficient`), for VP9 — what we serve
   * — and H.264, which we don't: {vp9_smooth_max, vp9_hw_max, h264_smooth_max,
   * h264_hw_max}, 0 for none. The data an H.264 ladder would have to be justified by.
   */
  | "decode_capabilities"
  | "ws_disconnected"
  /*
   * The account funnel and coarse usage (2026-08-22). Still anonymous — nothing here
   * calls identify() — and still never per-clip: `fap_saved` carries the SURFACE it
   * happened on and nothing about which clip, so "what was fapped" stays in our own
   * tables where the header says it lives.
   */
  | "auth_opened"
  /*
   * Sign-up became two screens on 2026-09-20, and this is the seam between them: fired
   * when stage 1 validates and stage 2 appears. It is the missing middle of the only
   * funnel this change can break — auth_opened{mode:"register"} -> signup_stage_two ->
   * account_created — without which a wizard that loses half its sign-ups between the
   * screens looks exactly like one that never did.
   *
   * NO PROPERTIES, deliberately. Everything on stage 1 is either the user's own words
   * or a protected characteristic; "they reached screen two" is the whole fact.
   */
  | "signup_stage_two"
  /*
   * A save on /settings, by which half — `section: "details" | "security"`, a closed
   * two-value vocabulary. It answers the one question that page raises and that the
   * "nobody gets nagged" decision rests on: does anyone ever fill these in. If the
   * answer turns out to be nobody, that decision is the thing to revisit.
   *
   * Never a VALUE: not the email, not the birthday, not the gender, not a question id,
   * and emphatically not an answer. `$pathname` already says /settings.
   */
  | "settings_saved"
  | "account_created"
  | "signed_in"
  | "auth_failed"
  | "signed_out"
  | "fap_saved"
  | "whats_playing_picked"
  /*
   * WHAT a screen plays and for HOW LONG — by kind (quick setup / collection / custom
   * view) and, for quick setups and collections, by name. Never which clip, and a custom
   * view reports kind only (its name is the user's own words). `view_watched` rides a
   * 5-minute heartbeat plus the remainder on change, so an hours-long television still
   * counts even when its tab is killed without a goodbye.
   */
  | "view_played"
  | "view_watched"
  /*
   * Collection and view UPKEEP (2026-08-25): the act on the container, never its name,
   * its id, or any clip. Renames carry nothing — the name is the user's own words. The
   * clone of a public collection is ONE act (`collection_cloned` with an item_count),
   * not one `added_to_collection` per clip. `view_created` counts INTENT (the builder,
   * a wall fork) at the UI, not api.views.createTemp — WhatsPlaying and MintedScreen
   * mint temp views as play-plumbing that `whats_playing_picked`/`view_played` already
   * count. Admin surfaces stay untracked, like their pageviews.
   */
  /*
   * GOONCHA (2026-08-25): the robot check's own funnel — shown → passed, with failed /
   * rerolled / abandoned as the leaks, and degraded as the alarm (the catalog could not
   * grade a grid and waved the caller through). Props are the PROMPT (a closed facet
   * vocabulary — "Latina", never a clip, never user text), the door (mode), and the
   * attempt count. Nothing about which tiles.
   */
  | "gooncha_shown"
  | "gooncha_passed"
  | "gooncha_failed"
  | "gooncha_rerolled"
  | "gooncha_abandoned"
  | "gooncha_degraded"
  /*
   * The region editor's own funnel (2026-08-26). `view_saved`/`view_edited` carry the
   * SHAPE of the build (client/src/lib/viewShape.ts): layout as a preset id or
   * "custom", source-kind counts, the facet-tag combo (`filters` — a closed vocabulary,
   * the GOONCHA-prompt precedent; free text is counted as `custom_terms`, never sent),
   * sync usage, and a collections COUNT (their names are the user's own words). The
   * three below are the editor's verbs: which layout was picked from the palette, and
   * the canvas link toggle in each direction. Who is building — signed in or not — is
   * the `signed_in` super prop the session store already stamps on everything.
   */
  | "layout_picked"
  | "regions_linked"
  | "regions_unlinked"
  /*
   * GoonLords Gold's conversion pipeline (2026-08-26): gate_hit → upsell_shown →
   * patreon_cta_clicked → (server) patreon_linked, broken down by gate — which limit converts,
   * and which only frustrates (gate hits that never convert). The server also sends
   * gold_granted {source: "patreon"} when the linked membership grants Premium days — at
   * the link itself and on later renewals. Props are the GATE or
   * SOURCE from a closed vocabulary (lib/gold.ts types them; screens / views /
   * collections / regions / layout, plus page / lapse / menu for impressions) —
   * never a price, a username, or an id. gate_hit fires at every pre-empt intercept, even
   * with the modal already open: a refused click is the frustration signal, and a
   * disabled button that never reports is invisible frustration — which is why no
   * Gold gate renders one (controls stay clickable and intercepted).
   */
  | "gold_gate_hit"
  | "gold_upsell_shown"
  /*
   * The daily wall allowance ran out on this screen (2026-09-10): {wallMinutes: the
   * allowance, tiles: what the wall had, signedIn}. Once per screen per UTC day. The
   * gate event (gold_gate_hit{gate:"allowance"}) fires beside it; this one carries the
   * shape of the wall that got shrunk, which the tuning question needs.
   */
  | "wall_allowance_exhausted"
  /*
   * The menu's Premium row (2026-09-09), {gate: "menu" | "lapse"} — split out of
   * gold_upsell_shown, which it used to share and thereby inflated fourteenfold: a row
   * in a menu is an impression, the dialog is the pitch, and a funnel whose first step
   * cannot tell them apart cannot say how many pitches went unclicked. Once per page
   * load, like the row itself.
   */
  | "gold_row_shown"
  /*
   * The Patreon leg of the same funnel (2026-09-04): Premium is a Patreon membership
   * now, so the dialog's CTA is two steps — {step: "subscribe"} opens the Patreon
   * checkout, {step: "connect"} starts the account link. Against the server's
   * patreon_linked {status} this says how many who left for Patreon came back, and how
   * many came back without a membership behind them. {source} (2026-09-08) is the
   * GoldCtaSource vocabulary plus "settings" and "thanks" (PatreonCtaSource): which
   * gate, menu, card or page sent them —
   * lib/gold.ts closes it.
   */
  | "patreon_cta_clicked"
  /*
   * The per-PICK grain of the same funnel: a source landing on a region (one event per
   * decision, however many tiles the selection spreads it over). Keyword picks carry
   * the facet combo under facetCombo's closed-vocabulary rule; collection picks carry
   * the community collection's name — the view_played precedent — with every own
   * collection bucketed as the literal "own", because those names are the user's words.
   */
  | "region_source_set"
  /*
   * THE WALL'S REGION CONTROLS (2026-08-27): whether the on-glass controls earn their
   * keep — each act counted, never the clip and never a word of the user's. Space's
   * skip-all carries how many regions moved (a layout fact, the `regions` grain
   * region_source_set already uses); its hovered-tile sibling `screen_skip_region`
   * carries nothing — "one region" is the whole fact. Sound and loop carry the
   * toggle's direction;
   * volume the settled 0–100 level, once per drag (the same settle the PUT debounce
   * waits for). `region_feed_opened` is the popup funnel's top — opens that never land
   * a region_source_set are a picker that isn't working. And the two "it didn't take"
   * signals: `wall_edit_failed` when a wall-side PUT is refused (a builtin's
   * session-local edit, an expired temp), `sound_refused` when the browser turns a
   * region's sound back off — a feature that LOOKS on and stays silent.
   */
  | "screen_skip_all"
  | "screen_skip_region"
  | "region_sound_set"
  | "region_volume_set"
  /** The Photo time chip's slider settled (once per drag, like volume): `seconds` the new
   *  hold, `from_seconds` the old. A number on a fixed ladder, never a clip. */
  | "region_photo_time_set"
  | "region_loop_set"
  | "region_mirror_set"
  | "region_feed_opened"
  /*
   * INFINITE SCROLL — enough to say whether the surface earns its keep, and no more.
   * `infinity_page` is a swipe landing on page N (page 0 is arrival, already
   * view_played) with which scroll and whether the page was themed (Keyword Scroll's
   * roulette can fall back to random); `infinity_double_tap_fap` is a double tap, and
   * `infinity_sound` is the retired tap for sound (2026-10-31); `infinity_left` is the sitting's summary — how deep, how long —
   * fired once when the stage leaves or the page hides. With view_watched (time, by
   * kind) and fap_saved (surface "infinity"), that is the whole readout: picks, depth,
   * minutes, faps per minute, against the walls. Depths and seconds, never a clip and
   * never the keyword.
   */
  | "infinity_page"
  /** The auto-scroll wait was set from the ☰ row — {scroll, sec}; 0 is off. Against
   *  infinity_page{auto: true} (2026-09-14: the page event says whether a hand or the
   *  clock turned it): whether people let it run. */
  | "infinity_auto_scroll"
  | "infinity_sound"
  | "infinity_double_tap_fap"
  | "infinity_left"
  /*
   * BUILD A SLUT (2026-09-19). The funnel, one event a step: `infinity_build_pick`
   * {scroll, stage} per round picked (the stage WORD only, never the trait);
   * `infinity_build_complete` {scroll, gold} when the fifth pick lands (how many
   * finish); `infinity_build_wall` {scroll, gold} when the first wall of her is dealt;
   * gold_gate_hit("build") when the ask lies over it, and GoldDialog's own gold_upsell /
   * patreon_cta("build") from there. `infinity_left` carries `rounds` on the picking scrolls,
   * so an abandoned build says which round it died on. `infinity_start_over` {scroll} is
   * the do-over, either door (☰ or the card).
   */
  | "infinity_build_pick"
  | "infinity_build_complete"
  | "infinity_build_wall"
  | "infinity_start_over"
  /* The ask card's other door (2026-09-19): "Go to a Random View" — a coin between
   * Colorwave and the Community Wall; `to` says which. */
  | "infinity_build_leave"
  /*
   * GOONETTE OF THE HILL (2026-09-19): `infinity_battle_pick` {scroll, round, kept, streak}
   * per pick — did the champion stay, and for how many rounds now; the preference itself
   * (which clips) goes to POST /api/battles/pick, never here. `infinity_battle_resume`
   * {scroll, round, streak} when a signed-in game is picked up again. Depth per sitting is
   * infinity_left's `rounds`.
   */
  | "infinity_battle_pick"
  | "infinity_battle_resume"
  | "wall_edit_failed"
  | "sound_refused"
  /*
   * THE WALLS' BUY FUNNEL (2026-08-29, `wall` added 2026-09-09 for the Premium Wall):
   * `wall_buy_opened` {tier, status, wall} when the dialog mounts (the GoldDialog
   * impression precedent), `wall_checkout_started` {tier, clips, wall} at the moment
   * of handoff to the processor. `wall` is the closed WallId vocabulary ("main" |
   * "premium") every wall-scoped event now carries, client and server alike, so a
   * second wall was a breakdown, not a fork of the dashboard. The far end of the funnel
   * is server-side truth — wall_square_sold on the webhook — so the client never
   * claims a sale, only the approach to one. Never a username, never a clip.
   */
  /*
   * THE SHOP'S FUNNEL: shop_viewed {products}, shop_product_viewed {slug, cents},
   * shop_cart_added {slug, cents, from}, shop_cart_viewed {items, cents}, shop_signin_prompted
   * {items, cents}, shop_checkout_opened {items, cents}, shop_checkout_started {items, cents},
   * shop_checkout_failed {items, status}. The far end is server-side truth, shop_order_paid.
   * Never an address.
   */
  | "shop_viewed"
  /** The ad slot showed a shop product — {slug, placement, reason: rotation | ad_fallback | member}. */
  | "shop_promo_shown"
  /** The ad slot's house card sent someone to a product — {slug, placement, reason}. */
  | "shop_promo_clicked"
  /** A category's See all page — {category, products}. */
  | "shop_category_viewed"
  | "shop_product_viewed"
  | "shop_cart_added"
  | "shop_cart_viewed"
  | "shop_signin_prompted"
  | "shop_checkout_opened"
  | "shop_checkout_started"
  | "shop_checkout_failed"
  | "wall_buy_opened"
  | "wall_checkout_started"
  /** A free-at-the-floor claim went through — {tier, wall}. The client CAN claim this one
   *  (unlike a sale): there is no processor leg, the placement was live before the
   *  redirect. The server's wall_square_claimed_free is still the funnel's far end. */
  | "wall_free_claimed"
  /** Somebody chose a file for a square — {bytes, wall}. Counted at the ATTEMPT, because
   *  the interesting number is how many of these never become a wall_upload_received: that
   *  gap is the format allowlist and the size cap refusing people. */
  | "wall_upload_started"
  /** THE VAULT (2026-09-09), five moments and no ids. opened {usedBytes, maxBytes, items}
   *  is the panel on screen, once per page load — the top of every vault funnel and the
   *  fill level of the people who look. upload_started {bytes} is the ATTEMPT — the
   *  server's vault_upload_received is the landing, and the gap between them is the size
   *  cap and the quota refusing people (the server's vault_upload_refused says which).
   *  quota_hit (no props) is the 409 that said the vault is full; expand_clicked (no
   *  props) is the tap on "Expand for 👑N". So quota_hit → expand_clicked →
   *  faps_spent{kind:vault} (server) is the sink's funnel. played {via: collection |
   *  picker} is Your Uploads being put on a screen — the reason the vault exists, and
   *  the number that says whether people who fill it ever watch it. Bytes and counts are
   *  the only numbers that ride — never a filename, never a key. */
  | "vault_opened"
  | "vault_upload_started"
  | "vault_quota_hit"
  | "vault_expand_clicked"
  | "vault_played"
  /** A surface guide opened — {surface, source: first-run|info}. First-run measures the
   *  nag's reach; info measures "this page did not explain itself the first time". */
  | "guide_shown"
  /** ...and closed — {surface, forever}. `forever` is the split that matters: a reader
   *  who pressed "don't show this again" was taught; one who only pressed "Got it"
   *  will be asked again next visit, by design. */
  | "guide_dismissed"
  /** A /how section's "go do it" link was followed — {surface}. The page's $pageview
   *  says somebody arrived (a Discord or Patreon answer links it cold); this says the
   *  page CONVERTED — reading turned into standing on the surface it explained. */
  | "howto_link_clicked"
  /** A checkout POST was refused — {tier, status, wall}. The status IS the diagnosis: 423s
   *  are contention (a good problem), 400s are the picker offering something the
   *  server won't sell (a bug), 5xx is the processor. Success has wall_square_sold;
   *  without this, failure had nothing. */
  | "wall_checkout_failed"
  /*
   * FAPS, the currency's own funnel (2026-09-04). pill_opened {source: pill|menu|
   * wall_short|profile|link} is the door in (`link` = the /faps/redeem deep link a shop
   * code arrives through); chest_claimed {amount} and code_redeemed
   * {amount} are the two things a person DOES in there (the server has the money truth
   * in faps_earned — these are the acts);
   * insufficient_shown {needed} is the frustration the wall's short-balance 409 produces,
   * and get_more_clicked {door: chest|redeem|premium|shop} says which way out of it
   * people take. welcome_shown (no props) is the one-time greeting on the grant — the top
   * of the funnel, once per account ever.
   *
   * Iteration 1 (2026-09-08) added the two moments it built. THE CAP MOMENT: cap_hit
   * {tier: basic|premium} is a tap the server said was capped (once per page load — after
   * the cap every tap is capped, and one is the fact); cap_nudge_shown and
   * cap_nudge_clicked (no props) are the toast that sells Premium off it, so the funnel
   * reads cap_hit → nudge_shown → nudge_clicked → gold_upsell_shown{faps_cap} →
   * patreon_cta_clicked{faps_cap} → gold_granted. (The toast is NOT a gold_upsell_shown: the
   * dialog it opens fires that, and counting both would double the gold funnel's top.)
   * THE WALL DOORS: wall_clicked {source: welcome|wallet|redeemed, short} is a Faps surface
   * sending someone to the wall — the welcome card, the wallet's Spend-it line, the
   * post-redeem link — with whether they could afford a small square at that moment, so
   * welcome_shown → wall_clicked → wall_square_bought_faps (server) is a funnel and "do
   * the short ones go anyway" is a breakdown. Amounts are our numbers, doors, sources and
   * tiers closed vocabularies (lib/faps.ts types them) — never a code, never a username.
   */
  | "faps_pill_opened"
  | "faps_chest_claimed"
  | "faps_quest_completed"
  | "faps_code_redeemed"
  | "faps_insufficient_shown"
  | "faps_get_more_clicked"
  | "faps_welcome_shown"
  | "faps_cap_hit"
  | "faps_cap_nudge_shown"
  | "faps_cap_nudge_clicked"
  | "faps_wall_clicked"
  /* THE BIDDING SINK (2026-09-08). unseated_shown / unseated_reclaim_clicked (no props)
   * are the take-it-back card a displaced holder sees after being sniped and the tap that
   * sends them to reclaim the square — so the server's wall_holder_unseated →
   * unseated_shown → unseated_reclaim_clicked → wall_square_bought_faps is the "do people
   * fight back" funnel. Defends are counted server-side (wall_defended); the client adds
   * nothing there. */
  | "faps_unseated_shown"
  | "faps_unseated_reclaim_clicked"
  /** Which shelf the buyer opened — {tab: faps|collection|kw|top, wall}. The picker
   *  replaced free-text search on a hunch; this is the hunch's scoreboard. Collections
   *  are bucketed to the word, never an id. */
  | "wall_picker_tab"
  /*
   * THE FEED (2026-09-13). Counts of the act, never the post or the clip. opened {posts}
   * is the page on screen; picker_tab {tab} and upload_started {bytes} are the wall
   * picker's two events with the Feed's name on them (same picker, different door);
   * post_created {upload, collection, caption} against post_failed {status} is the composer's
   * funnel; comment_posted {reply} and the two deletes {by} are the thread's life; a
   * board name clicked {board} says which board people actually read.
   */
  | "feed_opened"
  | "feed_picker_tab"
  | "feed_upload_started"
  | "feed_post_created"
  | "feed_post_failed"
  | "feed_comment_posted"
  | "feed_post_deleted"
  | "feed_comment_deleted"
  | "feed_leaderboard_name_clicked"
  /*
   * GOON ROOMS (2026-09-19). Counts of the act — never a room's id, a member's name
   * or a clip. opened {rooms, house_rooms} is the list on screen (`rooms` counts member
   * rooms only, as it always has); created {public}; joined {role, house}
   * against join_failed {reason} is the door's funnel; link_copied {public};
   * message_sent {kind: text|image}; moderated {action} is the host acting.
   *
   * A ROOM IS THE HOST'S SCREEN (2026-09-19), so most of what happens in one is an
   * event the screen already sends — a page turned, a feed popup opened, a crown, a
   * session started, the pencil's fork — and those are told apart by the `room_role`
   * SUPER property (host | member, null off a room) rather than duplicated. What is new
   * here: pointed {kind, scroll?, from: picker|arrows|fork} is the host changing what
   * everyone watches; repointed {role} is a screen hearing it; left {role, durationSec}
   * is a stay (a reload never sends it); chat_folded {open}; leave_prompted {choice:
   * leave|stay} is the host's confirm.
   */
  | "rooms_opened"
  | "room_created"
  | "room_joined"
  | "room_join_failed"
  | "room_link_copied"
  | "room_message_sent"
  | "room_moderated"
  | "room_pointed"
  | "room_repointed"
  | "room_left"
  | "room_chat_folded"
  | "room_leave_prompted"
  | "collection_renamed"
  | "collection_edited"
  | "collection_published"
  | "collection_deleted"
  | "collection_cloned"
  /*
   * BULK SELECT on a collection page (issue #12, 2026-10-28). The select mode's own
   * funnel — entered → bulk_action (→ undone) — plus how it ended. All closed
   * vocabularies and counts; never a clip, never a collection's name or id.
   *
   *   entered  {entry: button|checkbox|long_press|keyboard, device}
   *   exited   {reason: action|cancel, selected_count}
   *   action   {action: move|add|remove, count, destination: existing|new|null,
   *             collection_type: collection|faps, select_all, skipped_duplicates,
   *             limit_blocked}
   *   undone   {action, count}
   *
   * A bulk action does NOT also fire one added_to_collection / removed_from_collection
   * per clip: a 500-clip move would be 500 events in a second, past posthog-js's own
   * burst limit, so the per-item chart would show a random fraction of it. `count` is the
   * per-item number, and the single paths now carry `via: "single"` — the share done in
   * bulk is sum(collection_bulk_action.count) over that plus itself.
   */
  | "collection_select_mode_entered"
  | "collection_select_mode_exited"
  | "collection_bulk_action"
  | "collection_bulk_action_undone"
  | "removed_from_collection"
  | "view_created"
  | "view_edited"
  | "view_renamed"
  | "view_deleted"
  /**
   * The View panel's own controls (added with the panel, 2026-08-27). All closed
   * vocabularies: the icon comes from the curated quick picks or the picker groups
   * (the free-text input is gone), `via` says which; aspect is a preset label;
   * place is front/back. No user words travel.
   */
  | "view_icon_set"
  | "view_aspect_set"
  | "adaptive_layout_toggled"
  | "region_reordered"
  /*
   * THE MASTER AUDIO PANEL AND THE SPONSOR CREDIT (2026-08-28). Two questions, and the
   * second one is somebody else's money.
   *
   * Does the panel earn its place — is it found at all, and do the two new modes get used,
   * or did a wall of sixteen only ever want mute/unmute? And does the partnership WORK:
   * the channel plays, the credit is opened, the link is followed. Without the last of
   * those we cannot tell a partner how many people we sent them, which is the entire trade
   * they supplied a track for.
   *
   * `channel` and `partner` are curated first-party SLUGS, and sending them is in-rule.
   * The ban in this file's header is on per-CLIP behaviour and on the user's own words;
   * these are copy we wrote, already public at the channels endpoint, and they identify a
   * SPONSOR rather than a person — the same class of fact as layout_picked's template id.
   * The line is the id and only the id: never the channel name, never the artist (a third
   * party's name, whose shape we do not control), never a url. lib/channels.ts and
   * lib/partners.ts close those props at compile time, the way lib/gold.ts does.
   *
   * `channel_played` fires when the CHANNEL CHANGES, never on a loop and never on a
   * resume — a track that re-reported every time it looped would be a heartbeat, and one
   * television once sent 618 of those.
   */
  | "audio_panel_opened"
  | "audio_mode_set"
  | "master_volume_set"
  | "channel_volume_set"
  | "channel_played"
  | "partner_shown"
  | "partner_link_clicked"
  /**
   * One per AdSlot MOUNT, when its outcome resolves (2026-10-21, Analytics): {zone,
   * placement: in-feed|footer|sidebar|rail, outcome: filled|no_iframe|blocked|house_card,
   * reason?: blocked|timeout (house_card only), viewport_width}. `zone` is the JuicyAds
   * zone id from public settings — a network slot, not a person. Never fires for ads off,
   * safe mode or members. The mapping and the fire-once latch: control/lib/adSlotEvent.ts.
   */
  | "ad_slot_rendered";

/**
 * Anything matching these is stripped from a URL before it leaves the browser.
 *
 * Media urls are signed (`exp`/`sig`, see server/src/media-access.ts) and screen links
 * carry device ids and redemption codes. Pageviews already normalize their paths, but an
 * EXCEPTION carries urls we never chose — a stack frame, a failing request, the page url
 * at throw time — so the scrub has to happen centrally, on the way out, or error tracking
 * quietly reintroduces the leak that the pageview normalizer closed.
 */
const SENSITIVE_PARAMS = /\b(exp|sig|code|token|k)=[^&\s"']+/gi;

/**
 * Id- and credential-bearing PATH segments.
 *
 * Separate from SENSITIVE_PARAMS because these are not query parameters: `/claim/ABCD12`
 * is a pairing code and `/control/<token>` a handoff token, both sitting in the path
 * itself. useRouteAnalytics normalizes these for pageviews, but an EXCEPTION carries urls
 * nobody chose — a stack frame, a failing request, the page url at throw time — so the
 * same collapse has to happen here or an error thrown on the claim screen ships the code.
 *
 * `claim` stays although its ROUTE is gone: a stale bookmark still puts that path in the
 * address bar, and the url at throw time is exactly the kind of thing nobody chose. See
 * the note in useRouteAnalytics.ts — the two are kept in step, including this.
 *
 * `media` is a clip: `/api/media/<key>` and `/media/<key>.webm` name one file, and a file
 * is a clip, which is exactly the thing this module exists not to send. It is here because
 * it was seen arriving — a media url opened directly in a tab fell through to the SPA and
 * produced a pageview with the key in it.
 *
 * `u` and `feed` (2026-10-21): `/u/<username>` names a PERSON, and `$current_url` /
 * `$pathname` are stamped raw on every event by the SDK, so a profile visit used to ship
 * the username on the pageview, the page-leave and every event fired while it was open.
 * They collapse to `/u/:id` and `/feed/:id` like every other segment. A segment that is
 * already a placeholder (`:id`, `:username` — what the route normalizer sends as
 * `pathname`) is left as it is, so the normalized pageview path keeps its value.
 *
 * Kept deliberately in step with ID_SEGMENTS in useRouteAnalytics.ts; the checks assert
 * the credential-bearing ones are present in both.
 */
const ID_PATH = /\/(view\/d|play\/collection|views|claim|control|browse|collections|media|room|u|feed|shop\/orders)\/(?!:(?:id|username)\b)[^/\s"'?&]+/gi;

function scrub(value: string): string {
  return value.replace(SENSITIVE_PARAMS, "$1=[redacted]").replace(ID_PATH, "/$1/:id");
}

/*
 * ---- Session replay backstops (2026-10-21, replay masking tip 1) ---------------------
 *
 * `maskTextSelector` masks TEXT under a `data-ph-mask` marker (lib/replayMask.ts) and
 * nothing else. Two things leak past it, and both are closed here rather than in the
 * PostHog UI, because client masking options shadow the project's remote ones:
 *
 *  1. Attributes. rrweb records `href`, `title`, `aria-label`, `download`, `placeholder`
 *     verbatim, so a masked name still rode out in `href="/u/<name>"` beside it.
 *     maskReplayAttribute: on or inside a marker, those values are masked like the text
 *     (same length, `*`); everywhere, URL attributes have their person / post / room id
 *     segments collapsed, and a `download` filename (always derived from user data here)
 *     is masked.
 *  2. URLs the recorder sends of its own accord: the replay's page URL (meta `href`,
 *     `$url_changed`) and, if remote config turns network capture on, each captured
 *     request's URL. posthog-js routes BOTH through maskCapturedNetworkRequestFn.
 *
 * The URL rewrite reuses ID_PATH — the same pattern the before_send scrub runs — but only
 * acts on its `/u/`, `/feed/`, `/room/`, `/view/d/`, `/claim/` and `/control/` matches, plus
 * the API's own spellings of those ids (`/rooms/<id>`, `/feed/posts/<id>`,
 * `/feed/comments/<id>`, `/bans/<name>`, `/devices/<id>`, `/screens/<id>`).
 * Everything else in a URL (other paths, query, host) is left exactly as it was, so a
 * replay's network panel still says which endpoint was called.
 */

/**
 * The ID_PATH segments a replay URL collapses: a person, a post, a room (= its invite), and
 * (2026-10-25) a device id, a pairing code and a handoff token. The recorder sends
 * `$url_changed` before useRouteAnalytics stops recording on the new route, so the hop from
 * the recorded front door to `/view/d/<deviceId>` used to carry the id out — and holding a
 * device id is the server's trust bar for acting as that device's owner.
 */
const REPLAY_ID_SEGMENTS = new Set(["u", "feed", "room", "view/d", "claim", "control", "shop/orders"]);
/** `/feed/<word>` and `/control/<word>` that name an endpoint, not an id. */
const REPLAY_NAMED_PATHS = /^\/(feed\/(posts|comments|leaderboards)|control\/(adopt|handoff))$/i;
/**
 * The API's spellings of the same ids: /api/rooms/<id>, /api/feed/posts/<id>, .../bans/<name>,
 * /api/devices/<deviceId>, /api/screens/<deviceId>/hold, /api/screens/pending/<screenId>. The
 * endpoint words under devices/screens (`open`, `claim`, `pending`) are not ids and stay.
 */
const REPLAY_API_ID_PATH =
  /\/(rooms|feed\/posts|feed\/comments|bans|devices|screens\/pending|screens)\/(?!:(?:id|username)\b)(?!(?:open|claim|pending)(?:[/?#\s"']|$))[^/\s"'?&]+/gi;

/**
 * A `code` query parameter: a Faps shop code (`/faps/redeem?code=…`, a credential worth
 * money, redeemed by whoever opens the link). RedeemLink drops it from the address bar on
 * arrival, but a replay's page URL can be taken first. Same placeholder as the before_send
 * scrub's SENSITIVE_PARAMS. Every `code=` goes, including the `/screen?code=0|1` flag:
 * telling a flag from a code by its shape is a guess, and a replay loses nothing by it.
 */
const REPLAY_SENSITIVE_PARAMS = /\b(code|k)=[^&#\s"']+/gi;

/** A URL (absolute or a path) with its person, post and room ids collapsed to `:id`, and
 *  a `code` query parameter redacted. */
export function maskReplayUrl(url: string): string {
  return url
    .replace(REPLAY_SENSITIVE_PARAMS, "$1=[redacted]")
    .replace(REPLAY_API_ID_PATH, "/$1/:id")
    .replace(ID_PATH, (whole: string, seg: string) =>
      REPLAY_ID_SEGMENTS.has(seg.toLowerCase()) && !REPLAY_NAMED_PATHS.test(whole) ? `/${seg}/:id` : whole,
    );
}

/** Attributes that carry words a person wrote when they sit on or inside a marker. */
const REPLAY_TEXT_ATTRS = new Set([
  "title",
  "aria-label",
  "aria-description",
  "aria-roledescription",
  "aria-valuetext",
  "placeholder",
  "alt",
  "label",
  "download",
  "href",
  "xlink:href",
  "src",
  "srcset",
]);
/** Attributes holding a URL: their ids are collapsed wherever they are. */
const REPLAY_URL_ATTRS = new Set(["href", "xlink:href", "src", "action", "formaction"]);

/** rrweb's own text mask: same length, every non-space character a `*`. */
const maskChars = (value: string): string => value.replace(/\S/g, "*");

function insideMask(element: Element | undefined): boolean {
  return typeof element?.closest === "function" && element.closest(MASK_SELECTOR) !== null;
}

/**
 * session_recording.maskAttributeFn. Called for every non-empty attribute of every
 * recorded node, so the name is checked first and everything else returns untouched.
 */
export function maskReplayAttribute(name: string, value: string, element?: Element): string {
  const n = name.toLowerCase();
  const textAttr = REPLAY_TEXT_ATTRS.has(n);
  const urlAttr = REPLAY_URL_ATTRS.has(n);
  if (!textAttr && !urlAttr) return value;
  if (n === "download" || (textAttr && insideMask(element))) return maskChars(value);
  return urlAttr ? maskReplayUrl(value) : value;
}

/*
 * Supplying maskCapturedNetworkRequestFn REPLACES posthog-js' default body scrub
 * (`scrubPayloads` in its replay config: a body containing a deny-listed word, a card
 * number or an SSN is replaced by a "redacted" note). This callback exists to rewrite
 * URLs, not to record more, so it re-applies that scrub with the SDK's own deny list —
 * and a broader card/SSN test, so it can only ever redact more than the default, never
 * less. Headers and sizes are untouched; the SDK strips credential headers before this
 * runs.
 */
const BODY_DENY = ["password", "secret", "passwd", "api_key", "apikey", "auth", "credentials", "mysql_pwd", "privatekey", "private_key", "token"];
const CARD_OR_SSN = /(?:\d[ -]?){13,19}|\b\d{3}-\d{2}-\d{4}\b/;
/**
 * A JSON body with a `code` field: POST /faps/redeem {code} (a Faps shop code) and POST
 * /screens/claim {code} (a pairing code), and /screens/pending's answer carrying one.
 * `code` is not on the SDK's deny list, and a substring match on it would redact half the
 * API ("encode", "barcode"), so it is matched as a JSON key.
 */
const CODE_FIELD = /"code"\s*:/;
function scrubReplayBody(body: string | null | undefined, label: "Request" | "Response"): string | null | undefined {
  if (body === null || body === undefined) return body;
  const text = String(body);
  if (CARD_OR_SSN.test(text)) return `[SessionRecording] ${label} body redacted`;
  if (CODE_FIELD.test(text)) return `[SessionRecording] ${label} body redacted as might contain: code`;
  const hit = BODY_DENY.find((word) => text.includes(word));
  return hit ? `[SessionRecording] ${label} body redacted as might contain: ${hit}` : body;
}

/** session_recording.maskCapturedNetworkRequestFn: the page URL and request URLs. */
export function maskReplayRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  if (typeof request.name === "string") request.name = maskReplayUrl(request.name);
  if (request.requestBody !== undefined) request.requestBody = scrubReplayBody(request.requestBody, "Request");
  if (request.responseBody !== undefined) request.responseBody = scrubReplayBody(request.responseBody, "Response");
  return request;
}

/**
 * The before_send scrub, over everything a URL can ride in. `properties` holds the SDK's
 * own `$current_url`, `$pathname`, `$referrer` and `$exception` frames as well as ours
 * (posthog-js has stamped them by the time before_send runs), so the deep walk reaches
 * them; `$set` / `$set_once` sit BESIDE `properties` and carry `$initial_current_url` /
 * `$initial_pathname` — the first page a person landed on, which can be somebody's
 * profile — so they are walked too. Exported so tools/checks can push real events
 * through it; this module is import-safe without a window.
 */
export function scrubEvent<T extends { properties?: Record<string, unknown>; $set?: unknown; $set_once?: unknown }>(
  event: T,
): T {
  if (event.properties) event.properties = scrubDeep(event.properties) as typeof event.properties;
  if (event.$set) event.$set = scrubDeep(event.$set) as T["$set"];
  if (event.$set_once) event.$set_once = scrubDeep(event.$set_once) as T["$set_once"];
  return event;
}

/** Recursively scrub strings in an event payload, leaving structure intact. */
function scrubDeep(value: unknown, depth = 0): unknown {
  if (depth > 6) return value;
  if (typeof value === "string") return scrub(value);
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = scrubDeep(v, depth + 1);
    }
    return out;
  }
  return value;
}

// The cross-origin exception drop lives with the script that causes it — see
// isMaskedCrossOriginException in lib/juicyads.ts.

/**
 * Per-event-name rate limit.
 *
 * `media_error` is the reason this exists. A wall whose age token has lapsed fails on
 * EVERY tile on EVERY advance — six regions advancing every ~19s is ~1,150 errors an hour
 * from one browser. Uncapped, the first real incident would also be the moment the
 * month's quota disappeared, which is exactly when you want the data most.
 *
 * A fixed window rather than a debounce: the first few of a burst are the informative
 * ones (they carry the kind and the rung), and the thousandth tells you nothing the
 * first did not.
 */
const RATE_LIMITS: Partial<Record<AppEvent, { max: number; windowMs: number }>> = {
  media_error: { max: 5, windowMs: 60_000 },
  // Same arithmetic: a starved wall stalls on every tile at once.
  media_stall: { max: 5, windowMs: 60_000 },
  // Down is at most one step per 10s cooldown, up one per minute: ten a minute is a
  // flapping budget, and the eleventh says nothing the tenth did not.
  playback_budget_changed: { max: 10, windowMs: 60_000 },
  ws_disconnected: { max: 5, windowMs: 60_000 },
  // A screen whose poll keeps lapsing re-mints on every retry; five say "flapping".
  screen_code_reissued: { max: 5, windowMs: 60_000 },
  // fapClick is a spammable tally bump — a mashed button is one fap, not thirty events.
  //
  // `fap_clicked` stood beside this and is GONE. It was the same act counted a second
  // time: this branch instruments the five UI call sites and carries the surface each
  // happened on, that one fired centrally in api.ts with no surface at all. Two branches
  // adding the same event under two names is not two events, and keeping both would have
  // doubled every fap in the funnel.
  fap_saved: { max: 10, windowMs: 60_000 },
  // Auto-scroll at five seconds turns twelve pages a minute for as long as it is left
  // running; thirty a minute says "on", the thirty-first says nothing the first did not.
  infinity_page: { max: 30, windowMs: 60_000 },
  // A thread can run hot; ten a minute says "in a conversation", the eleventh says nothing new.
  feed_comment_posted: { max: 10, windowMs: 60_000 },
  // A room's chat runs hot; the same ten a minute the thread gets.
  room_message_sent: { max: 10, windowMs: 60_000 },
  // A reconnecting socket flaps: the first join says "in", the sixth says nothing new.
  room_joined: { max: 5, windowMs: 60_000 },
  room_join_failed: { max: 5, windowMs: 60_000 },
  // One button, pressed again to be sure.
  room_link_copied: { max: 5, windowMs: 60_000 },
  // The view autosave settles every 1.2s during an editing session (ViewEditor's
  // AUTOSAVE_MS), and wall edits can burst under key-repeat. Ten a minute says
  // "actively editing"; the eleventh says nothing the tenth did not.
  view_edited: { max: 10, windowMs: 60_000 },
  // A stuck human retries fast; ten a minute says "struggling", the eleventh adds
  // nothing. Same shape for the reroll button, which is made to be pressed.
  gooncha_failed: { max: 10, windowMs: 60_000 },
  gooncha_rerolled: { max: 10, windowMs: 60_000 },
  // Editor verbs under key-repeat and indecision: ten a minute says "exploring the
  // palette" / "rewiring the wall"; the eleventh says nothing the tenth did not.
  layout_picked: { max: 10, windowMs: 60_000 },
  regions_linked: { max: 10, windowMs: 60_000 },
  regions_unlinked: { max: 10, windowMs: 60_000 },
  // The wall's region controls: every one is a button that invites mashing — a held
  // Space, a toggled toggle, a popup opened and closed and opened — and the first few
  // of a burst are the informative ones.
  screen_skip_all: { max: 10, windowMs: 60_000 },
  screen_skip_region: { max: 10, windowMs: 60_000 },
  region_sound_set: { max: 10, windowMs: 60_000 },
  region_volume_set: { max: 10, windowMs: 60_000 },
  region_photo_time_set: { max: 10, windowMs: 60_000 },
  region_loop_set: { max: 10, windowMs: 60_000 },
  region_mirror_set: { max: 10, windowMs: 60_000 },
  region_feed_opened: { max: 10, windowMs: 60_000 },
  wall_edit_failed: { max: 5, windowMs: 60_000 },
  sound_refused: { max: 5, windowMs: 60_000 },
  // Auditioning sources is click-through-the-list behaviour; twenty a minute keeps the
  // signal while a browse-everything session stops counting as twenty decisions.
  region_source_set: { max: 20, windowMs: 60_000 },
  // A user mashing a refused control is ONE frustration, not thirty; the mark/
  // suppressed_after mechanism keeps the burst visible. patreon_cta_clicked has no limit —
  // it is rare and deliberate.
  gold_gate_hit: { max: 10, windowMs: 60_000 },
  gold_upsell_shown: { max: 10, windowMs: 60_000 },
  gold_row_shown: { max: 10, windowMs: 60_000 },
  wall_allowance_exhausted: { max: 3, windowMs: 60_000 },
  // The vault's impression and its play: a panel mounts on every visit and a pick is a
  // tap that can be mashed, so both get the gold rows' ceiling.
  vault_opened: { max: 10, windowMs: 60_000 },
  vault_played: { max: 10, windowMs: 60_000 },
  // The FAPS pill sits in the bar on every surface and is made to be poked; a mashed
  // Buy button on a short balance is one frustration. Chest, redeem and the doors out
  // are unlimited — rare, deliberate, and each one is a decision.
  faps_pill_opened: { max: 10, windowMs: 60_000 },
  faps_insufficient_shown: { max: 5, windowMs: 60_000 },
  // The audio panel. Its button is ALSO the mute toggle, so it is pressed for reasons that
  // have nothing to do with the panel; the modes are a row of four made to be tried; and a
  // fader settles once per drag but a hand can settle it over and over.
  audio_panel_opened: { max: 10, windowMs: 60_000 },
  audio_mode_set: { max: 10, windowMs: 60_000 },
  master_volume_set: { max: 10, windowMs: 60_000 },
  channel_volume_set: { max: 10, windowMs: 60_000 },
  // Stepping channels is click-through-the-list behaviour — region_source_set's twenty.
  channel_played: { max: 20, windowMs: 60_000 },
  partner_shown: { max: 5, windowMs: 60_000 },
  // partner_link_clicked has NO limit, deliberately: it is rare, it is intentional, and it
  // is the conversion itself — the number a partner is owed.
};
const windows = new Map<string, { count: number; resetAt: number }>();

/**
 * `send` as normal, `mark` for the one that trips the limit, `drop` for the rest.
 *
 * The tripping event is still sent, tagged `suppressed_after`, so a burst leaves a
 * visible mark in the data. Silently dropping the tail would make a wall failing on every
 * tile look identical to one that recovered after five errors — the opposite of what the
 * event is for.
 */
type Verdict = "send" | "mark" | "drop";

function verdictFor(event: AppEvent): Verdict {
  const limit = RATE_LIMITS[event];
  if (!limit) return "send";
  const now = Date.now();
  const w = windows.get(event);
  if (!w || now >= w.resetAt) {
    windows.set(event, { count: 1, resetAt: now + limit.windowMs });
    return "send";
  }
  w.count++;
  if (w.count <= limit.max) return "send";
  return w.count === limit.max + 1 ? "mark" : "drop";
}

/**
 * The SDK, once it has loaded — and the queue of anything captured before it did.
 *
 * posthog-js is imported DYNAMICALLY, inside init, rather than at the top of this file.
 * It runs `_getHashParam(window.location.hash, ...)` at module scope, so merely importing
 * it where there is no `window` throws before a line of our code runs. That is not
 * hypothetical: `track` is called from lib/api.ts, lib/ws.ts and RegionPlayer.tsx, and
 * tools/checks import all three under tsx — so a static import took an unrelated check
 * (card-grammar) down at import time, nowhere near anything about analytics.
 *
 * Loading it lazily means importing `track` costs nothing but this module. Only
 * initAnalytics, called from main.tsx in a browser, ever pulls the SDK in.
 */
let ph: PostHog | null = null;
let started = false;

/**
 * Events captured between init being called and the SDK arriving over the network.
 *
 * The first pageview is fired by a router effect, which can easily beat a dynamic import,
 * and dropping it would lose the landing page from every session — the one pageview
 * acquisition analysis is actually about. Bounded because a load that never resolves
 * (blocked by an extension, offline) must not grow this forever.
 */
const QUEUE_MAX = 50;
const queued: Array<{ event: string; props?: Record<string, unknown> }> = [];

/**
 * Super properties — stamped on every event from now on — accepted before the SDK is up.
 *
 * `role` (what this tab IS: screen, control, remote, landing, admin) and `signed_in` are
 * registered by the components that know, and those mount before the dynamic import of
 * the SDK resolves. Held here and applied in `loaded`, BEFORE the queue drains, so the
 * first pageview carries them too. After load, straight through to the SDK.
 *
 * `register` persists in the browser, so a tab keeps its last role across reloads until a
 * role-root mounts and says otherwise — which every one of them does, on mount.
 */
let pendingSuper: Record<string, unknown> = {};

/**
 * Everything `registerSuper` has ever set, mirrored — because `reset()` throws it away.
 *
 * posthog.reset() clears the SDK's whole persistence, and `register` lives in it, so a
 * sign-out would otherwise strip `environment`, `role` and `signed_in` off every event
 * that followed. `environment` is the one that really hurts: every insight and dashboard
 * filters on it, so events missing it are invisible rather than merely unlabelled.
 *
 * Nothing puts them back on its own — `role` is registered by a useLayoutEffect
 * (useAnalyticsRole) that does not re-run on logout — so `resetIdentity` replays this.
 */
let superProps: Record<string, unknown> = {};

export function registerSuper(props: Record<string, unknown>): void {
  if (!started) return;
  superProps = { ...superProps, ...props };
  if (ph) ph.register(props);
  else pendingSuper = { ...pendingSuper, ...props };
}

/**
 * The identity change that beat the SDK, held for the same reason as pendingSuper.
 *
 * BOTH directions have to be held, and the reset is the one that matters most. A
 * signed-out page load takes the synchronous path through session.init() — no token, no
 * await — so it runs LONG before the dynamic import resolves. Drop that reset on the
 * floor and posthog-js rehydrates the previous session's identity from localStorage and
 * the browser goes on reporting as an account that signed out, permanently. Found by
 * signing out and reloading; no amount of reading the source showed it.
 */
let pendingIdentity: { id: string; props: Record<string, unknown> } | null = null;
let pendingReset = false;

/**
 * Tie everything from here on to an account.
 *
 * `id` is the account's uuid, NEVER the username. A username is display; identifying by
 * it would split one person in two the first time anyone renames. The name rides along as
 * a person PROPERTY instead, and the project's `person_display_name_properties` is set to
 * `username`, so PostHog prints the name anywhere it would otherwise print the uuid.
 *
 * Safe against the scrub: `before_send` runs scrubDeep over `$set`, but SENSITIVE_PARAMS
 * needs a `key=value` and ID_PATH needs a leading `/segment/`, so a bare username passes
 * through untouched. Asserted in tools/checks/analytics-identity.mts rather than assumed.
 */
/**
 * This browser's anonymous analytics id, for a signed-out purchase: the server sends the paid
 * event under it so the order joins this browser's funnel. Null when analytics is off or the
 * SDK hasn't loaded (an ad blocker, say), and the order then counts without the join.
 */
export function anonymousAnalyticsId(): string | null {
  try {
    const id = ph?.get_distinct_id();
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

export function identifyUser(id: string, props: Record<string, unknown>): void {
  if (!started) return;
  if (ph) ph.identify(id, props);
  else {
    pendingIdentity = { id, props };
    pendingReset = false;
  }
}

/**
 * Forget the account, on sign-out.
 *
 * reset() mints a fresh anonymous distinct_id, which is the point: a shared browser must
 * not go on attributing the next visitor's browsing to whoever just signed out. It also
 * wipes the super properties, so they go straight back on — see superProps.
 */
export function resetIdentity(): void {
  if (!started) return;
  pendingIdentity = null;
  if (!ph) {
    pendingReset = true;
    return;
  }
  // Only on a real identified -> anonymous transition. session.init() reports "nobody" on
  // EVERY signed-out page load, and reset() mints a new distinct_id every time it is
  // called — so resetting unconditionally would give an anonymous visitor a brand-new
  // identity on every reload and shatter them into one person per pageview.
  if (ph.get_property("$user_state") !== "identified") return;
  ph.reset();
  ph.register({ environment: ENVIRONMENT, ...superProps });
}

/** A MediaError's message, bounded for an analytics property; null when there is none. */
export function mediaErrorMessage(err: MediaError | null | undefined): string | null {
  const m = err?.message?.trim();
  return m ? m.slice(0, 120) : null;
}

/**
 * Would `track(event)` send right now? NON-consuming.
 *
 * For a caller with work to do before it can track — RegionPlayer probes a failed url to
 * learn why it failed — that is wasted on an event the limiter is about to drop.
 */
export function wouldSend(event: AppEvent): boolean {
  if (!started) return false;
  const limit = RATE_LIMITS[event];
  if (!limit) return true;
  const w = windows.get(event);
  return !w || Date.now() >= w.resetAt || w.count <= limit.max;
}

function emit(event: string, props?: Record<string, unknown>): void {
  if (ph) ph.capture(event, props);
  else if (started && queued.length < QUEUE_MAX) queued.push({ event, props });
}

export function initAnalytics(): void {
  if (started || !KEY) return;
  started = true;
  void import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
    api_host: HOST,
    // With a custom api_host the SDK can no longer infer where the PostHog app lives;
    // this is what the toolbar and its links point at, never where events go.
    ui_host: "https://us.posthog.com",
    // SPA: the router owns navigation, so pageviews are captured by useRouteAnalytics.
    // Left on, this fires once on load and then never again as the user moves around.
    capture_pageview: false,
    // Leaving is genuinely useful for bounce/dwell and costs one event.
    capture_pageleave: true,
    // Off at init — see the header. The route hook turns it on for control surfaces only
    // (setSessionRecording below); the wall and credential routes never record.
    disable_session_recording: true,
    session_recording: {
      // Layout and taps, never content: clips and stills render as blocked boxes in a
      // replay, and nothing typed is ever captured.
      blockSelector: "video, img, canvas",
      maskAllInputs: true,
      // What a PERSON wrote — usernames, titles, captions, comments — is marked with
      // lib/replayMask.ts's MASK, and rrweb masks every text node under a marker.
      maskTextSelector: "[data-ph-mask]",
      // The two backstops the selector cannot be: attributes (href=/u/<name>, title,
      // aria-label, download) and the URLs the recorder sends itself. See maskReplayUrl.
      maskAttributeFn: maskReplayAttribute,
      maskCapturedNetworkRequestFn: maskReplayRequest,
    },
    /*
     * Autocapture OFF, deliberately.
     *
     * Measured: clicking through the age gate alone produced 5 autocapture events against
     * 2 pageviews. The control panel — layout editing, tag pickers, the cave editor — is
     * far clickier, and what it records is generated selectors on tiles and controls,
     * which answer no question anyone would actually ask of this app. Modelled at 100
     * users it is roughly half of all volume and cuts the free tier's headroom from ~485
     * users to ~230.
     *
     * The named events below are the replacement: fewer, and each one is a question.
     *
     * NOTE heatmaps and web vitals are NOT controlled here — they are pushed by remote
     * config from the project settings, so they must also be turned off in the PostHog UI
     * (Project Settings -> Autocapture) or they keep arriving regardless of this file.
     */
    autocapture: false,
    /*
     * No person profiles unless something explicitly identifies a user. `identifyUser` is
     * that something, and lib/session.ts is its only caller: an account, at the moment it
     * signs in or is restored from a token.
     *
     * "identified_only" rather than "always" is what keeps the rest of the site anonymous.
     * Browsing without an account creates no person at all — not a thinner one, none — so
     * the only people PostHog knows are the ones who chose to make an account. The cost of
     * that choice is that pre-signup activity cannot be back-attributed once they do; the
     * funnel counts it, no person owns it. That is the right way round for an adult site.
     */
    person_profiles: "identified_only",
    // Unhandled errors and promise rejections. Everything is scrubbed by before_send.
    capture_exceptions: true,
    persistence: "localStorage+cookie",
    before_send: (event) => {
      if (!event) return null;
      // Third-party throws the browser refuses to place — see isMaskedCrossOriginException.
      if (event.event === "$exception" && isMaskedCrossOriginException(event.properties)) return null;
      return scrubEvent(event);
    },
    // Loaded rather than configured in the UI, so a misconfigured project cannot start
    // recording sessions behind our back.
        loaded: (loadedPh) => {
          // FIRST: a sign-out that beat the SDK. posthog-js has just rehydrated the last
          // session's identity out of localStorage, and this is the only thing that
          // clears it — before anything below is registered onto it.
          if (pendingReset) {
            pendingReset = false;
            // Guarded like resetIdentity: only clear an identity that is actually there.
            // posthog-js has just rehydrated the last session from localStorage, so this
            // is true exactly when a signed-out load follows a signed-in one.
            if (loadedPh.get_property("$user_state") === "identified") loadedPh.reset();
          }
          loadedPh.register({ environment: ENVIRONMENT, ...pendingSuper });
          pendingSuper = {};
          // Before the queue drains below, so anything captured while the SDK was in
          // flight lands on the account rather than on the anonymous id it replaced.
          if (pendingIdentity) {
            loadedPh.identify(pendingIdentity.id, pendingIdentity.props);
            pendingIdentity = null;
          }
          if (!ENV.PROD) loadedPh.debug();
        },
      });
      ph = posthog;
      // Anything captured while the SDK was in flight. Drained in order, after
      // `register` has run, so the queued events carry `environment` too.
      for (const q of queued.splice(0)) posthog.capture(q.event, q.props);
    })
    .catch(() => {
      // A blocked or failed SDK load must not take the app with it: analytics is the
      // least important thing on the page. Leave `ph` null so everything stays a no-op.
      queued.length = 0;
    });
}

/** A named product event. Rate-limited where the name warrants it. */
/**
 * Turn session recording on or off for the current route. Called by useRouteAnalytics on
 * every navigation; idempotent (posthog-js ignores a redundant start/stop). Recording
 * never begins on its own — the init leaves it disabled — so a route that forgets to
 * call this is simply not recorded, which is the safe failure.
 */
export function setSessionRecording(on: boolean): void {
  if (!ph) return;
  if (on) ph.startSessionRecording();
  else ph.stopSessionRecording();
}

export function track(event: AppEvent, props?: Record<string, unknown>): void {
  if (!started) return;
  const verdict = verdictFor(event);
  if (verdict === "drop") return;
  const extra = verdict === "mark" ? { suppressed_after: RATE_LIMITS[event]?.max } : undefined;
  emit(event, extra ? { ...props, ...extra } : props);
}

/**
 * Capture a pageview for an SPA route change.
 *
 * `$current_url` is passed explicitly: PostHog reads `window.location` at capture time,
 * and React Router has already updated it by the time an effect runs, but being explicit
 * is what makes that a guarantee rather than an ordering coincidence.
 */
export function capturePageview(pathname: string): void {
  if (!started) return;
  emit("$pageview", { $current_url: window.location.href, pathname });
}
